import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import TabNavigation from "./ui/TabNavigation";
import { motion, AnimatePresence } from "framer-motion";
import toast from "../utils/notify";
import {
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  EyeIcon,
  Cog6ToothIcon,
  PlusIcon,
  XMarkIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentTextIcon,
  BeakerIcon,
  FireIcon,
  SparklesIcon,
  UserIcon,
  CalendarIcon,
  TagIcon
} from "@heroicons/react/24/outline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AutomatedRiskManagement = () => {
  const { user, token } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("assessment");
  
  // Data states
  const [riskAssessments, setRiskAssessments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  
  // Modal states
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  
  // New risk form state
  const [newRisk, setNewRisk] = useState({
    title: '',
    description: '',
    category: '',
    inherentRisk: {
      likelihood: 1,
      impact: 1,
      score: 1
    },
    existingControls: [],
    residualRisk: {
      likelihood: 1,
      impact: 1,
      score: 1
    },
    treatmentPlan: {
      strategy: '',
      actions: [],
      owner: '',
      timeline: '',
      budget: 0
    }
  });

  // Mock automated risk data
  const mockRiskAssessments = [
    {
      id: "risk-001",
      title: "Cybersecurity Threat - Ransomware Attack",
      description: "Risk of ransomware compromising critical systems and data",
      category: "Cybersecurity",
      inherentRisk: {
        likelihood: 4,
        impact: 5,
        score: 20,
        severity: "Critical"
      },
      existingControls: [
        {
          id: "ctrl-001",
          name: "Endpoint Detection & Response",
          effectiveness: 85,
          implementation: "Automated",
          coverage: 95,
          lastUpdated: "2024-01-15T10:00:00Z"
        },
        {
          id: "ctrl-002", 
          name: "Employee Security Training",
          effectiveness: 70,
          implementation: "Manual",
          coverage: 100,
          lastUpdated: "2024-01-10T14:30:00Z"
        },
        {
          id: "ctrl-003",
          name: "Network Segmentation",
          effectiveness: 90,
          implementation: "Automated",
          coverage: 80,
          lastUpdated: "2024-01-12T09:15:00Z"
        }
      ],
      residualRisk: {
        likelihood: 2,
        impact: 4,
        score: 8,
        severity: "High"
      },
      treatmentPlan: {
        strategy: "Mitigate",
        actions: [
          {
            action: "Implement Zero Trust Architecture",
            priority: "High",
            timeline: "3 months",
            owner: "IT Security Team",
            cost: 150000,
            status: "In Progress"
          },
          {
            action: "Enhanced Backup & Recovery System",
            priority: "High", 
            timeline: "2 months",
            owner: "Infrastructure Team",
            cost: 75000,
            status: "Planning"
          },
          {
            action: "Advanced Threat Intelligence",
            priority: "Medium",
            timeline: "4 months",
            owner: "Security Operations Center",
            cost: 85000,
            status: "Not Started"
          }
        ],
        totalBudget: 310000,
        expectedRiskReduction: 60,
        targetResidualRisk: 3.2
      },
      assessment: {
        assessedBy: "Risk Assessment AI",
        assessmentDate: "2024-01-15T12:00:00Z",
        lastReviewed: "2024-01-15T12:00:00Z",
        nextReview: "2024-02-15T12:00:00Z",
        confidence: 92
      },
      status: "Active",
      trend: "Improving"
    },
    {
      id: "risk-002",
      title: "Data Privacy Compliance - GDPR Violation",
      description: "Risk of GDPR non-compliance resulting in regulatory fines",
      category: "Regulatory",
      inherentRisk: {
        likelihood: 3,
        impact: 4,
        score: 12,
        severity: "High"
      },
      existingControls: [
        {
          id: "ctrl-004",
          name: "Data Classification System",
          effectiveness: 80,
          implementation: "Automated",
          coverage: 90,
          lastUpdated: "2024-01-14T11:20:00Z"
        },
        {
          id: "ctrl-005",
          name: "Privacy Impact Assessments", 
          effectiveness: 75,
          implementation: "Manual",
          coverage: 85,
          lastUpdated: "2024-01-08T16:45:00Z"
        }
      ],
      residualRisk: {
        likelihood: 2,
        impact: 3,
        score: 6,
        severity: "Medium"
      },
      treatmentPlan: {
        strategy: "Mitigate",
        actions: [
          {
            action: "Implement Automated Data Discovery",
            priority: "High",
            timeline: "2 months",
            owner: "Privacy Team",
            cost: 95000,
            status: "In Progress"
          },
          {
            action: "Enhanced Consent Management",
            priority: "Medium",
            timeline: "3 months", 
            owner: "Legal Team",
            cost: 45000,
            status: "Planning"
          }
        ],
        totalBudget: 140000,
        expectedRiskReduction: 50,
        targetResidualRisk: 3.0
      },
      assessment: {
        assessedBy: "Compliance AI Engine",
        assessmentDate: "2024-01-14T15:30:00Z",
        lastReviewed: "2024-01-14T15:30:00Z", 
        nextReview: "2024-02-14T15:30:00Z",
        confidence: 88
      },
      status: "Active",
      trend: "Stable"
    },
    {
      id: "risk-003",
      title: "Cloud Infrastructure - Service Outage",
      description: "Risk of cloud service disruption affecting business operations",
      category: "Operational",
      inherentRisk: {
        likelihood: 3,
        impact: 3,
        score: 9,
        severity: "Medium"
      },
      existingControls: [
        {
          id: "ctrl-006",
          name: "Multi-Region Deployment",
          effectiveness: 95,
          implementation: "Automated",
          coverage: 100,
          lastUpdated: "2024-01-13T08:00:00Z"
        },
        {
          id: "ctrl-007",
          name: "Health Monitoring & Alerts",
          effectiveness: 88,
          implementation: "Automated",
          coverage: 95,
          lastUpdated: "2024-01-15T07:30:00Z"
        }
      ],
      residualRisk: {
        likelihood: 1,
        impact: 2,
        score: 2,
        severity: "Low"
      },
      treatmentPlan: {
        strategy: "Accept",
        actions: [
          {
            action: "Chaos Engineering Implementation",
            priority: "Low",
            timeline: "6 months",
            owner: "DevOps Team",
            cost: 25000,
            status: "Planning"
          }
        ],
        totalBudget: 25000,
        expectedRiskReduction: 20,
        targetResidualRisk: 1.6
      },
      assessment: {
        assessedBy: "Infrastructure Risk AI",
        assessmentDate: "2024-01-13T14:00:00Z",
        lastReviewed: "2024-01-13T14:00:00Z",
        nextReview: "2024-04-13T14:00:00Z",
        confidence: 95
      },
      status: "Active",
      trend: "Improving"
    },
    {
      id: "risk-004", 
      title: "Third-Party Vendor - Supply Chain Risk",
      description: "Risk from critical vendor dependencies and potential disruptions",
      category: "Third-Party",
      inherentRisk: {
        likelihood: 2,
        impact: 4,
        score: 8,
        severity: "Medium"
      },
      existingControls: [
        {
          id: "ctrl-008",
          name: "Vendor Risk Assessments",
          effectiveness: 70,
          implementation: "Manual",
          coverage: 80,
          lastUpdated: "2024-01-10T10:00:00Z"
        },
        {
          id: "ctrl-009",
          name: "Contractual Security Requirements",
          effectiveness: 85,
          implementation: "Manual", 
          coverage: 100,
          lastUpdated: "2024-01-05T13:20:00Z"
        }
      ],
      residualRisk: {
        likelihood: 1,
        impact: 3,
        score: 3,
        severity: "Low"
      },
      treatmentPlan: {
        strategy: "Transfer",
        actions: [
          {
            action: "Cyber Insurance Enhancement",
            priority: "Medium",
            timeline: "1 month",
            owner: "Risk Management",
            cost: 50000,
            status: "In Progress"
          },
          {
            action: "Diversify Critical Vendors",
            priority: "Medium",
            timeline: "6 months",
            owner: "Procurement",
            cost: 120000,
            status: "Planning"
          }
        ],
        totalBudget: 170000,
        expectedRiskReduction: 40,
        targetResidualRisk: 1.8
      },
      assessment: {
        assessedBy: "Third-Party Risk AI",
        assessmentDate: "2024-01-10T16:00:00Z",
        lastReviewed: "2024-01-10T16:00:00Z",
        nextReview: "2024-04-10T16:00:00Z",
        confidence: 83
      },
      status: "Active", 
      trend: "Stable"
    }
  ];

  useEffect(() => {
    if (token) {
      loadRiskData();
    }
  }, [token]);

  const loadRiskData = async () => {
    try {
      setLoading(true);
      
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Load Vanta Risk Management dashboard data
      const dashboardResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk/dashboard`, {
        headers
      });
      
      if (!dashboardResponse.ok) {
        throw new Error(`Dashboard API failed: ${dashboardResponse.status}`);
      }
      
      const dashboardData = await dashboardResponse.json();
      
      // Transform Vanta Risk Management API response to match component structure
      const transformedDashboard = {
        totalRisks: dashboardData.overview?.total_risks || 0,
        criticalRisks: dashboardData.overview?.critical_risks || 0,
        highRisks: dashboardData.overview?.high_risks || 0,
        mediumRisks: dashboardData.overview?.medium_risks || 0,
        lowRisks: dashboardData.overview?.low_risks || 0,
        averageResidualRisk: dashboardData.overview?.average_risk_score || 0,
        controlEffectiveness: dashboardData.treatment_metrics?.treatment_effectiveness || 0,
        automatedControls: dashboardData.treatment_metrics?.active_treatments || 0,
        totalBudgetAllocated: 50000, // Default value for now
        riskAppetiteCompliance: dashboardData.risk_appetite_status?.appetite_compliance_rate || 0,
        recentActivities: dashboardData.recent_activities || []
      };
      
      setDashboard(transformedDashboard);

      // Load risk register data
      const risksResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/risk/register?limit=50`, {
        headers
      });
      
      if (!risksResponse.ok) {
        throw new Error(`Risk register API failed: ${risksResponse.status}`);
      }
      
      const risksData = await risksResponse.json();
      
      // Transform API data to match component structure
      const transformedRisks = (risksData.risks || []).map(risk => ({
        id: risk.id,
        title: risk.title || risk.description || 'Untitled Risk',
        category: risk.category,
        description: risk.description,
        inherentRisk: {
          likelihood: risk.likelihood_score,
          impact: risk.impact_score,
          score: risk.inherent_risk_score,
          severity: risk.severity
        },
        existingControls: risk.controls || [],
        residualRisk: {
          likelihood: risk.residual_likelihood_score || risk.likelihood_score,
          impact: risk.residual_impact_score || risk.impact_score,
          score: risk.residual_risk_score || risk.inherent_risk_score,
          severity: risk.residual_severity || risk.severity
        },
        treatmentPlan: {
          strategy: risk.treatment_strategy || 'Accept',
          actions: risk.treatment_actions || [],
          timeline: risk.treatment_timeline || 'TBD',
          owner: risk.owner || 'Unassigned',
          totalBudget: risk.treatment_budget || 0,
          status: risk.status || 'Draft'
        },
        assessment: {
          assessedBy: risk.assessed_by || 'Vanta Risk Engine',
          assessmentDate: risk.assessment_date || new Date().toISOString(),
          lastReviewed: risk.last_reviewed || new Date().toISOString(),
          nextReview: risk.next_review_date || new Date(Date.now() + 90*24*60*60*1000).toISOString(),
          confidence: risk.confidence_score || 85
        },
        lastReviewed: risk.last_reviewed || new Date().toISOString(),
        nextReview: risk.next_review_date || new Date(Date.now() + 90*24*60*60*1000).toISOString(),
        confidence: risk.confidence_score || 85,
        status: risk.status || 'Active',
        trend: risk.trend || 'Stable'
      }));
      
      setRiskAssessments(transformedRisks);

    } catch (error) {
      console.error('Error loading risk data:', error);
      toast.error('Failed to load risk management data');
      
      // Fallback to mock data if API fails
      setRiskAssessments(mockRiskAssessments);
      setDashboard({
        totalRisks: mockRiskAssessments.length,
        criticalRisks: mockRiskAssessments.filter(r => r.inherentRisk.severity === 'Critical').length,
        highRisks: mockRiskAssessments.filter(r => r.inherentRisk.severity === 'High').length,
        averageResidualRisk: mockRiskAssessments.reduce((acc, r) => acc + r.residualRisk.score, 0) / mockRiskAssessments.length,
        controlEffectiveness: 82.5,
        automatedControls: mockRiskAssessments.reduce((acc, r) => 
          acc + r.existingControls.filter(c => c.implementation === 'Automated').length, 0
        ),
        totalBudgetAllocated: mockRiskAssessments.reduce((acc, r) => acc + r.treatmentPlan.totalBudget, 0)
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateRiskScore = (likelihood, impact) => {
    return likelihood * impact;
  };

  const getRiskSeverity = (score) => {
    if (score >= 15) return 'Critical';
    if (score >= 10) return 'High';
    if (score >= 5) return 'Medium';
    return 'Low';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'High': return 'text-orange-600 bg-orange-50 border-orange-200'; 
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'Improving': return <ArrowTrendingDownIcon className="w-4 h-4 text-green-600" />;
      case 'Worsening': return <ArrowTrendingUpIcon className="w-4 h-4 text-red-600" />;
      default: return <SparklesIcon className="w-4 h-4 text-blue-600" />;
    }
  };

  const AssessmentTab = () => (
    <div className="space-y-6">
      {riskAssessments.map((risk) => (
        <motion.div
          key={risk.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{risk.title}</h3>
                <div className="flex items-center space-x-2">
                  {getTrendIcon(risk.trend)}
                  <span className="text-xs text-gray-500">{risk.trend}</span>
                </div>
              </div>
              <p className="text-gray-600 mb-3">{risk.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>Category: {risk.category}</span>
                <span>•</span>
                <span>Assessed by: {risk.assessment.assessedBy}</span>
                <span>•</span>
                <span>Confidence: {risk.assessment.confidence}%</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Inherent Risk */}
            <div className="bg-red-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-900 mb-3">Inherent Risk</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-red-700">Likelihood:</span>
                  <span className="font-medium">{risk.inherentRisk.likelihood}/5</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-red-700">Impact:</span>
                  <span className="font-medium">{risk.inherentRisk.impact}/5</span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-red-700">Risk Score:</span>
                  <span>{risk.inherentRisk.score}</span>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(risk.inherentRisk.severity)}`}>
                  {risk.inherentRisk.severity}
                </div>
              </div>
            </div>

            {/* Existing Controls */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3">
                Existing Controls ({risk.existingControls.length})
              </h4>
              <div className="space-y-2">
                {risk.existingControls.slice(0, 2).map((control) => (
                  <div key={control.id} className="text-xs">
                    <div className="flex justify-between">
                      <span className="text-blue-700 truncate">{control.name}</span>
                      <span className="font-medium">{control.effectiveness}%</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-1 py-0.5 rounded text-xs font-medium ${
                        control.implementation === 'Automated' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {control.implementation}
                      </span>
                      <span className="text-blue-600">{control.coverage}% coverage</span>
                    </div>
                  </div>
                ))}
                {risk.existingControls.length > 2 && (
                  <div className="text-xs text-blue-600">
                    +{risk.existingControls.length - 2} more controls
                  </div>
                )}
              </div>
            </div>

            {/* Residual Risk */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-green-900 mb-3">Residual Risk</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-700">Likelihood:</span>
                  <span className="font-medium">{risk.residualRisk.likelihood}/5</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-700">Impact:</span>
                  <span className="font-medium">{risk.residualRisk.impact}/5</span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-green-700">Risk Score:</span>
                  <span>{risk.residualRisk.score}</span>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(risk.residualRisk.severity)}`}>
                  {risk.residualRisk.severity}
                </div>
              </div>
            </div>
          </div>

          {/* Treatment Plan */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">Risk Treatment Plan</h4>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-600">Strategy:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  risk.treatmentPlan.strategy === 'Mitigate' ? 'bg-blue-100 text-blue-800' :
                  risk.treatmentPlan.strategy === 'Accept' ? 'bg-green-100 text-green-800' :
                  risk.treatmentPlan.strategy === 'Transfer' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {risk.treatmentPlan.strategy}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-2">Treatment Actions</h5>
                <div className="space-y-1">
                  {risk.treatmentPlan.actions.slice(0, 3).map((action, index) => (
                    <div key={index} className="text-xs flex items-center justify-between">
                      <span className="truncate text-gray-700">{action.action}</span>
                      <div className="flex items-center space-x-1 ml-2">
                        <span className={`px-1 py-0.5 rounded text-xs font-medium ${
                          action.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                          action.status === 'Planning' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {action.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Budget:</span>
                  <span className="font-medium">${risk.treatmentPlan.totalBudget.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expected Risk Reduction:</span>
                  <span className="font-medium text-green-600">{risk.treatmentPlan.expectedRiskReduction}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Target Residual Risk:</span>
                  <span className="font-medium">{risk.treatmentPlan.targetResidualRisk}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <div className="text-xs text-gray-500">
              Next Review: {new Date(risk.assessment.nextReview).toLocaleDateString()}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSelectedRisk(risk);
                  setShowRiskModal(true);
                }}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <EyeIcon className="w-4 h-4 mr-1" />
                Details
              </button>
              <button
                onClick={() => setShowAutomationModal(true)}
                className="inline-flex items-center px-3 py-1 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
              >
                <BoltIcon className="w-4 h-4 mr-1" />
                Automate
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  if (loading) {
    return (
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
    );
  }

  return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Ai_RiskRegister</h1>
          <p className="text-gray-600">
            AI-powered risk assessment with automated controls and treatment planning
          </p>
        </div>

        {/* Dashboard Overview */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Risks</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboard?.totalRisks || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <FireIcon className="w-8 h-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Critical/High</p>
                  <p className="text-2xl font-bold text-gray-900">{(dashboard?.criticalRisks || 0) + (dashboard?.highRisks || 0)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <BoltIcon className="w-8 h-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Automated Controls</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboard?.automatedControls || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <ChartBarIcon className="w-8 h-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg Residual Risk</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboard?.averageResidualRisk ? dashboard.averageResidualRisk.toFixed(1) : '0.0'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <TabNavigation
          tabs={[
            { id: 'assessment', name: 'Risk Assessment', icon: ExclamationTriangleIcon },
            { id: 'controls', name: 'Controls', icon: ShieldExclamationIcon },
            { id: 'automation', name: 'Automation', icon: BoltIcon },
            { id: 'reports', name: 'Reports', icon: DocumentTextIcon }
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'assessment' && (
            <motion.div
              key="assessment"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AssessmentTab />
            </motion.div>
          )}

          {activeTab === 'controls' && (
            <motion.div
              key="controls"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <ShieldExclamationIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Control Management</h3>
                <p className="text-gray-600 mb-4">
                  Monitor and manage automated risk controls with AI-powered effectiveness scoring
                </p>
                <button className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700">
                  <Cog6ToothIcon className="w-4 h-4 mr-2" />
                  Configure Controls
                </button>
              </div>
            </motion.div>
          )}

          {activeTab === 'automation' && (
            <motion.div
              key="automation"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <BoltIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Risk Automation</h3>
                <p className="text-gray-600 mb-4">
                  Configure automated risk assessment workflows and treatment plan generation
                </p>
                <button className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700">
                  <SparklesIcon className="w-4 h-4 mr-2" />
                  Enable Automation
                </button>
              </div>
            </motion.div>
          )}

          {activeTab === 'reports' && (
            <motion.div
              key="reports"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Risk Reports</h3>
                <p className="text-gray-600 mb-4">
                  Generate comprehensive risk management reports with automated insights
                </p>
                <button className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700">
                  <ChartBarIcon className="w-4 h-4 mr-2" />
                  Generate Report
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Risk Details Modal */}
        <AnimatePresence>
          {showRiskModal && selectedRisk && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">Risk Assessment Details</h3>
                    <button
                      onClick={() => {
                        setShowRiskModal(false);
                        setSelectedRisk(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{selectedRisk.title}</h4>
                      <p className="text-gray-700">{selectedRisk.description}</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-3">Risk Methodology</h5>
                        <div className="space-y-3">
                          <div className="bg-red-50 p-3 rounded-lg">
                            <h6 className="text-sm font-medium text-red-900">Inherent Risk</h6>
                            <p className="text-sm text-red-700">
                              Base risk without any controls: {selectedRisk.inherentRisk.score} ({selectedRisk.inherentRisk.severity})
                            </p>
                          </div>
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <h6 className="text-sm font-medium text-blue-900">Existing Controls</h6>
                            <p className="text-sm text-blue-700">
                              {selectedRisk.existingControls.length} controls with average effectiveness of{' '}
                              {Math.round(selectedRisk.existingControls.reduce((acc, c) => acc + c.effectiveness, 0) / selectedRisk.existingControls.length)}%
                            </p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg">
                            <h6 className="text-sm font-medium text-green-900">Residual Risk</h6>
                            <p className="text-sm text-green-700">
                              Risk after controls: {selectedRisk.residualRisk.score} ({selectedRisk.residualRisk.severity})
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-3">Treatment Plan Details</h5>
                        <div className="space-y-2">
                          {selectedRisk.treatmentPlan.actions.map((action, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-3">
                              <div className="flex justify-between items-start mb-1">
                                <h6 className="text-sm font-medium text-gray-900">{action.action}</h6>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  action.priority === 'High' ? 'bg-red-100 text-red-800' :
                                  action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {action.priority}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 space-y-1">
                                <div>Owner: {action.owner}</div>
                                <div>Timeline: {action.timeline}</div>
                                <div>Cost: ${action.cost.toLocaleString()}</div>
                                <div>Status: {action.status}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowRiskModal(false);
                        setSelectedRisk(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
  );
};

export default AutomatedRiskManagement;